import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { slug: "", city: "", level: "Pre", title: "", description: "", meta_description: "" };
const LEVELS = ["Pre", "Primary", "Middle", "Secondary", "Sr. Secondary"];

export default function SuperSEO() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);

  const load = async () => { const { data } = await api.get("/seo"); setItems(data); };
  useEffect(() => { load(); }, []);

  const auto = (city, level) => `${(city || "city").toLowerCase().replace(/\s+/g, "-")}-${level.toLowerCase().replace(/[.\s]+/g, "-")}`;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, slug: f.slug || auto(f.city, f.level) };
      await api.post("/seo", payload); toast.success("Saved"); setOpen(false); setF(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (s) => { setF(s); setOpen(true); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/seo/${id}`); load(); };

  return (
    <DashLayout variant="super" title="SEO & URL Configs">
      <div className="text-sm text-slate-500 mb-4">Configure per-URL SEO (title, description, meta description) for each City × Level page.</div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setF(empty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="new-seo-btn"><Plus className="w-4 h-4 mr-2" />Add URL SEO</Button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Slug</TableHead><TableHead>City</TableHead><TableHead>Level</TableHead><TableHead>Title</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`row-seo-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.slug}</TableCell>
                <TableCell>{s.city}</TableCell>
                <TableCell>{s.level}</TableCell>
                <TableCell className="font-semibold">{s.title}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-seo-${s.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-seo-${s.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>URL SEO Config</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="seo-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block">City *</Label><Input required value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="seo-city" /></div>
              <div><Label className="mb-1 block">Level *</Label>
                <Select value={f.level} onValueChange={(v) => setF({ ...f, level: v })}>
                  <SelectTrigger data-testid="seo-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="mb-1 block">Slug (auto if blank)</Label><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder={auto(f.city, f.level)} data-testid="seo-slug" /></div>
            <div><Label className="mb-1 block">Page Title *</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="seo-title" /></div>
            <div><Label className="mb-1 block">Description *</Label><Textarea rows={2} required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="seo-desc" /></div>
            <div><Label className="mb-1 block">Meta Description *</Label><Textarea rows={2} required value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} data-testid="seo-meta" /></div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="seo-submit">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
